import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Close } from '@mui/icons-material';
import axios from '../resources/axios';
import { TripHistoryDetails } from '../resources/types/contractsTypes';

interface RefundModalProps {
  open: boolean;
  rideId: string;
  userId: string;
  amount: number;
  historyDetails?: TripHistoryDetails;
  onClose: () => void;
  onSuccess: () => void;
}

const RefundModal: React.FC<RefundModalProps> = ({
  open,
  rideId,
  userId,
  amount,
  historyDetails,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubscription = amount === 0 || amount === '0' as any;

  const normalMinutes = historyDetails?.normal?.duration 
    ? Math.round(historyDetails.normal.duration / 60) 
    : 0;
  const pauseMinutes = historyDetails?.pause?.duration 
    ? Math.round(historyDetails.pause.duration / 60) 
    : 0;
  const reservationMinutes = historyDetails?.reservation?.duration 
    ? Math.round(historyDetails.reservation.duration / 60) 
    : 0;

  const [refundNormalMinutes, setRefundNormalMinutes] = useState<string>('0');
  const [refundPauseMinutes, setRefundPauseMinutes] = useState<string>('0');
  const [refundReservationMinutes, setRefundReservationMinutes] = useState<string>('0');
  const [refundUnlock, setRefundUnlock] = useState<boolean>(true);

  useEffect(() => {
    if (open && isSubscription) {
      setRefundNormalMinutes(normalMinutes.toString());
      setRefundPauseMinutes(pauseMinutes.toString());
      setRefundReservationMinutes(reservationMinutes.toString());
      setRefundUnlock(true);
    }
  }, [open, isSubscription, normalMinutes, pauseMinutes, reservationMinutes]);

  const sanitizeMinutes = (value: string, max: number): number => {
    const parsed = parseInt(value) || 0;
    return Math.max(0, Math.min(parsed, max));
  };

  const handleRefund = async (isFullRefund: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const userInfo = JSON.parse(localStorage.getItem('STORE_USER_INFO') || '{}');
      const token = userInfo.token;

      if (isSubscription) {
        const minutesToRefund = {
          normal: isFullRefund ? normalMinutes : sanitizeMinutes(refundNormalMinutes, normalMinutes),
          pause: isFullRefund ? pauseMinutes : sanitizeMinutes(refundPauseMinutes, pauseMinutes),
          reservation: isFullRefund ? reservationMinutes : sanitizeMinutes(refundReservationMinutes, reservationMinutes)
        };
        const unlockToRefund = isFullRefund ? 1 : (refundUnlock ? 1 : 0);

        if (!isFullRefund) {
          if (minutesToRefund.normal === 0 && minutesToRefund.pause === 0 && 
              minutesToRefund.reservation === 0 && !unlockToRefund) {
            setError(t('trips.refundModal.errorSelectAtLeastOne'));
            setLoading(false);
            return;
          }
        }

        await axios.post(
          '/contracts/refund',
          {
            contractId: rideId,
            amountBalance: 0,
            subscriptionAmounts: {
              normal: minutesToRefund.normal * 60,
              pause: minutesToRefund.pause * 60,
              reservation: minutesToRefund.reservation * 60,
              start: unlockToRefund
            }
          },
          {
            headers: {
              'x-access-token': token
            }
          }
        );
      } else {
        let amountToRefund = isFullRefund ? amount : parseFloat(refundAmount);

        if (!isFullRefund && (!refundAmount || amountToRefund <= 0)) {
          setError(t('trips.refundModal.errorEnterAmount'));
          setLoading(false);
          return;
        }

        if (amountToRefund > amount) {
          setError(t('trips.refundModal.errorAmountExceeds'));
          setLoading(false);
          return;
        }

        await axios.post(
          '/contracts/refund',
          {
            contractId: rideId,
            amountBalance: amountToRefund
          },
          {
            headers: {
              'x-access-token': token
            }
          }
        );
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Refund error:', err);
      setError(err.response?.data?.message || t('trips.refundModal.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRefundAmount('');
    setRefundNormalMinutes(normalMinutes.toString());
    setRefundPauseMinutes(pauseMinutes.toString());
    setRefundReservationMinutes(reservationMinutes.toString());
    setRefundUnlock(true);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('trips.refundModal.title')}</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>{t('trips.refundModal.tripCost')}:</strong> {amount} {t('trips.refundModal.currency')}
          </Typography>

          <Typography variant="body1" sx={{ mb: 3 }}>
            <strong>{t('trips.refundModal.subscription')}:</strong> {isSubscription ? t('trips.refundModal.yes') : t('trips.refundModal.no')}
          </Typography>

          {isSubscription ? (
            <>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                {t('trips.refundModal.spent')}:
              </Typography>
              <Box sx={{ mb: 2, pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • {t('trips.refundModal.trip')}: {normalMinutes} {t('trips.details.minutes')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • {t('trips.refundModal.pause')}: {pauseMinutes} {t('trips.details.minutes')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • {t('trips.refundModal.booking')}: {reservationMinutes} {t('trips.details.minutes')}
                </Typography>
                <Typography variant="body2">
                  • {t('trips.refundModal.unlock')}: 1
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                {t('trips.refundModal.refund')}:
              </Typography>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('trips.refundModal.tripMin')}
                  value={refundNormalMinutes}
                  onChange={(e) => setRefundNormalMinutes(e.target.value)}
                  sx={{ mb: 1.5 }}
                  inputProps={{ min: 0, max: normalMinutes }}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label={t('trips.refundModal.pauseMin')}
                  value={refundPauseMinutes}
                  onChange={(e) => setRefundPauseMinutes(e.target.value)}
                  sx={{ mb: 1.5 }}
                  inputProps={{ min: 0, max: pauseMinutes }}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label={t('trips.refundModal.bookingMin')}
                  value={refundReservationMinutes}
                  onChange={(e) => setRefundReservationMinutes(e.target.value)}
                  sx={{ mb: 1.5 }}
                  inputProps={{ min: 0, max: reservationMinutes }}
                  size="small"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={refundUnlock}
                      onChange={(e) => setRefundUnlock(e.target.checked)}
                    />
                  }
                  label={t('trips.refundModal.refundUnlock')}
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={() => handleRefund(false)}
                  sx={{
                    backgroundColor: '#2196F3',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#1976D2'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    }
                  }}
                >
                  {t('trips.refundModal.refundSelected')}
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={() => handleRefund(true)}
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    }
                  }}
                >
                  {t('trips.refundModal.refundFullTrip')}
                </Button>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {t('trips.refundModal.exit')}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                type="number"
                label={t('trips.refundModal.refundAmount')}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                sx={{ mb: 3 }}
                inputProps={{ min: 0, max: amount, step: 0.01 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={() => handleRefund(false)}
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    }
                  }}
                >
                  {t('trips.refundModal.refundMoney')}
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={() => handleRefund(true)}
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    }
                  }}
                >
                  {t('trips.refundModal.refundMoneyFull')}
                </Button>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {t('trips.refundModal.exit')}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RefundModal;
